/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import Backbone from 'backbone';
import $ from 'jquery';
import React, {ReactElement} from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {BufferContentView, TabContent, TabOptions} from 'buffers/BufferComps';
import BufferNavItemTemplate from 'buffers/BufferNavItemTemplate';
import * as bufferutil from 'buffers/BufferUtil';
import {COPYABLE_FOLDER_ITEMS, SHARE_LINK_ITEMS, STORABLE_ITEM_TYPES} from 'constants/ModelTypeConstants';
import FileState from 'filestate/FileState';
import {
  isEntryFileState,
  isFileStateWithModel,
  isSubtemplateFileState,
  isTemplateFileState,
} from 'filestate/FileStateTypedefs';
import FolderItemState from 'filestate/FolderItemState';
import {EntryFileState, SubtemplateFileState, TemplateFileState} from 'FileStates';
import ResourcePresenceView from 'nav/presence/ResourcePresenceView';
import ShareBar from 'nav/share/ShareBar';
import {BufferState, BufferStates, TabState, TabStates} from 'Navigation';
import BaseEntryProtocolTabState from 'notebook/entries/backbone/tabs/BaseEntryProtocolTab';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import EntityStore from 'stores/EntityStore';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {phraseToCamelCase} from 'util/NameUtil';
import {Optional} from 'util/Types';

import DocumentUpdatesManager from '../notebook/realtime/DocumentUpdatesManager';
import PresenceManager from './PresenceManager';

/** Whether the realtime presence avatars should render for the given workspace file state. */
function isPresenceEnabledForFileState(
  fileState: FileState
): fileState is EntryFileState | TemplateFileState | SubtemplateFileState {
  if (isEntryFileState(fileState)) {
    return true;
  }
  // Templates and sub-templates will eventually always support presence but for now we gate behind a flag
  return (
    (isTemplateFileState(fileState) || isSubtemplateFileState(fileState)) &&
    BenchlingConfig.ELN_REALTIME_TEMPLATE_UPDATES
  );
}

/**
 * View that creates an li.sectionTabsNavItem. This is the actual 'buffer tab'.
 */
class BufferNavItemView extends Backbone.View<TabState> {
  template!: typeof BufferNavItemTemplate;

  static initClass(): void {
    this.prototype.tagName = 'li';
    this.prototype.className = 'sectionTabs-navItem';
    this.prototype.template = BufferNavItemTemplate;
  }

  toggle(tab: TabState, active: boolean): void {
    if (active) {
      this.$el.addClass('active');
    } else {
      this.$el.removeClass('active');
    }
  }

  /**
   * Attaches a close button to the DOM rendered with BufferNavItemTemplate.
   * The actual closing is handled in BufferNavView.onClickTab.
   * TODO: BufferNavItemView should handle its own tab click and close logic
   */
  _renderCloseIcon(): void {
    if (this.model.get('closeable')) {
      renderToDOM(
        <BenchlingIcon icon="close" className="sectionTabs-navItem-closeBtn-svg" />,
        this.$('.js-sectionTabs-close')[0]
      );
    }
  }

  // TODO(BNCH-137395) This should have role=tab and it should be associated with the controlled tabpanel
  renderHtml(): void {
    const tabInfo = {...this.model.toJSON()};
    this.$el.attr('data-tab-id', this.model.id);
    this.$el.attr('aria-label', this.model.get('title'));

    const prefKey = this.model.attributes.preferenceKey
      ? phraseToCamelCase(this.model.attributes.preferenceKey)
      : 'noPreferenceKey';
    setTestAttrs(this.$el, {component: 'BufferNavItem', element: 'root', key: prefKey});

    // @ts-expect-error: we sometimes pass JQuery object instead of a string in these calls
    // TODO(BNCH-137395): this template is eminently convertable to React / renderToDOM
    this.$el.html($(this.template(tabInfo)).children());
    this._renderCloseIcon();
  }

  override remove(): Backbone.View<TabState> {
    return super.remove();
  }

  override render(): this {
    const tab = this.model;
    this.renderHtml();
    if (this.model.get('greyTab')) {
      this.$el.addClass('grey-tab');
    }
    if (this.model.get('active')) {
      this.$el.addClass('active');
    }
    if (!this.model.get('draggable')) {
      this.$el.addClass('no-drag-tab');
    }
    this.$el.data('tab', this.model);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(tab, 'change:title', this.renderHtml);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(tab, 'change:id', this.renderHtml);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(tab, 'change:closeable', this.renderHtml);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:active', this.toggle);
    const className = tab.get('className');
    if (className != null) {
      this.$el.addClass(className);
    }
    return this;
  }
}

BufferNavItemView.initClass();

/**
 * View that attaches to a div.buffer-tabs element in the editor and controls the rendering of individual
 * buffer tabs. Also contains the 'share bar', which has buttons for generating share links and duplicating
 * the workspace item, and the 'presence view', which in Entries allows users to see who else is also viewing
 * the entry.
 *
 * This view only controls one half of the workspace. (Even in a non-split workspace, two buffers worth of DOM
 * are rendered, the left buffer is hidden, and there is an inactive BufferNavView for it.)
 */
class BufferNavView extends Backbone.View<never> {
  _eventNamespace!: string;
  viewCache!: Array<BufferNavItemView>;
  $sectionTabs!: JQuery;
  $shareBar!: JQuery;
  ul!: JQuery;
  dropDownEl!: JQuery;
  $nav!: JQuery;
  shareBarMount: Optional<HTMLElement>;
  presenceMount: Optional<HTMLElement>;
  buffer: Optional<BufferState>;
  tabs: Optional<TabStates>;
  fileState: Optional<FileState>;

  static initClass(): void {
    this.prototype.events = {
      // @ts-expect-error
      'click .sectionTabs-navItem': 'onClickTab',
      'click .toggle': 'toggleDropdown',
      'click .overflow-list a': 'hideDropdown',
    };
  }

  override initialize(): void {
    this.renderItems = this.renderItems.bind(this);
    // Bind so the same reference can be passed to `removeEventListener` in `remove()`; otherwise (e.g. an
    // inline closure) this listener would never be removable.
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.closeFromOutside = this.closeFromOutside.bind(this);
    this._eventNamespace = `.bufferNavView-${this.cid}`;

    this.viewCache = [];
    this.$sectionTabs = this.$('.buffer-tabs-sectionTabs');
    this.$shareBar = this.$('.buffer-tabs-shareBarMount');
    this.ul = this.$sectionTabs.find('.sectionTabs-nav').eq(0);
    this.dropDownEl = this.$sectionTabs.find('.sectionTabs-moreDropdown').eq(0);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    document.addEventListener('mousedown', this.closeFromOutside, true);
    // eslint-disable-next-line no-multi-assign
    const $nav = (this.$nav = this.$('.sectionTabs-nav'));

    const that = this;
    this.$nav.sortable({
      placeholder: 'sectionTabs-navItemPlaceholder',
      forcePlaceholderSize: true,
      items: '> li:not(.no-drag-tab)',
      receive(event: JQueryEventObject, ui: JQueryUI.SortableUIParams) {
        const tab = ui.item.data('tab');
        if (tab instanceof BaseEntryProtocolTabState) {
          ui.sender.sortable('cancel');
          notifications.warn('Protocols cannot be moved from the left pane.');
        }
      },
      update(event: JQueryEventObject, ui: JQueryUI.SortableUIParams) {
        // The update event gets called twice: once for source buffer and again for destination buffer.
        // The source case does not need to be handled, so we skip it. See: http://stackoverflow.com/a/7340208
        // @ts-expect-error: TS says this will always be true, I think because of some odd `this` binding
        if (this !== ui.item.parent()[0]) {
          return;
        }

        const tab = ui.item.data('tab');
        const index = ui.item.index();
        _.defer(() => {
          (that.buffer!.collection as BufferStates).moveTab(tab, that.buffer!, index);
          $nav.sortable('refresh');
        });
      },
    });
  }

  onClickTab(e: JQueryEventObject): void {
    const node = $(e.currentTarget);
    const id = node.attr('data-tab-id');
    const tab = this.tabs!.get(id);
    if ($(e.target).closest('.sectionTabs-navItem-closeBtn').length) {
      tab.close();
    } else {
      tab.set('active', true);
    }
    // This is necessary because onClickTab is the callback for `click .sectionTabs-navItem`, and `li` in the
    // overflow list are also `.sectionTabs-navItem`.
    this.hideDropdown();
    tab.onClick();
    e.preventDefault();
  }

  closeFromOutside(e: JQueryMouseEventObject): void {
    const target = $(e.target);
    if (target.parents('.sectionTabs-moreDropdown').length === 0) {
      this.hideDropdown();
    }
  }

  hideDropdown(): void {
    this.$('.toggle').removeClass('active');
    this.$('.overflow-list').hide();
  }

  toggleDropdown(): void {
    this.$('.toggle').toggleClass('active');
    this.$('.overflow-list').toggle();
  }

  ulWidth(): number {
    // Note: We can't use $ul.width() because the UL has {flex: 1} to ensure that it has full width so that
    // tab drag-and-drop works properly.
    let width = 0;
    this.ul.find('li').each(function () {
      // @ts-expect-error: this errors but I don't want to deal with `this` binding
      width += $(this).outerWidth(true);
    });
    return width;
  }

  renderItems(): void {
    while (this.viewCache.length > 0) {
      this.viewCache.pop()!.remove();
    }

    this.tabs!.each((tab) => {
      if (!tab.showInNavBar()) {
        return;
      }
      const item = new BufferNavItemView({model: tab});
      this.viewCache.push(item);
      this.ul.append(item.render().el);
    });

    // Avoid .overflow styling from affecting the following computation.
    this.$sectionTabs.removeClass('overflow');
    this.$('.overflow-list').empty();
    // Move tabs out until it's okay.
    while (
      this.ulWidth() > this.$el.width() - this.dropDownEl.width() - this.$shareBar.width() &&
      this.ul.children().not('.active').length > 0
    ) {
      const li = this.ul.children().not('.active').last().addClass('sectionTabs-navItem--overflow').detach();
      this.$('.overflow-list').append(li);
    }
    this.$sectionTabs.toggleClass('overflow', this.$('.overflow-list li').length > 0);

    this.$nav.sortable('refresh');
  }

  handleTabDrag(): void {
    if (this.buffer!.get('shareTabsWithOtherBuffers')) {
      this.$nav.sortable({connectWith: '.sectionTabs-nav.js-share-tabs'});
    }
    this.$nav.toggleClass('js-share-tabs', this.buffer!.get('shareTabsWithOtherBuffers'));
  }

  override remove(): Backbone.View<never> {
    this.unmountShareBar();
    $(window).off(this._eventNamespace);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    document.removeEventListener('mousedown', this.closeFromOutside, true);
    return super.remove();
  }

  /**
   * Detach this nav from the previous file's state without destroying the view, for a pane that no
   * longer has a backing buffer. Stops the window/buffer/tab/file listeners set up in `update`, unmounts
   * the share bar and presence view (so the presence view stops running its users query for the old
   * file), and drops the rendered tab items. A later `update` re-attaches all of it, so the view stays
   * reusable. This is the difference from `remove`, which tears the view down for good.
   */
  deactivate(): void {
    $(window).off(this._eventNamespace);
    if (this.buffer != null) {
      this.stopListening(this.buffer);
      this.buffer = null;
    }
    const oldFile = isFileStateWithModel(this.fileState) ? this.fileState?.model() : null;
    if (oldFile != null) {
      this.stopListening(oldFile);
    }
    if (this.tabs != null) {
      this.stopListening(this.tabs);
      this.tabs = null;
    }
    this.fileState = null;
    this.unmountShareBar();
    this.unmountPresenceView();
    while (this.viewCache.length > 0) {
      this.viewCache.pop()!.remove();
    }
    this.$('.overflow-list').empty();
  }

  mountShareBar(): void {
    const shareBarMount = this.$('.buffer-tabs-shareBarMount')[0];
    if (shareBarMount == null || EntityStore.currentUser() == null) {
      return;
    }

    const fileType = this.fileState!.fileType();
    const isStorableItem = STORABLE_ITEM_TYPES.includes(fileType);
    if (
      !SHARE_LINK_ITEMS.includes(fileType) &&
      !COPYABLE_FOLDER_ITEMS.includes(fileType) &&
      !isStorableItem
    ) {
      this.unmountShareBar();
      return;
    }
    const activeTab = this.tabs?.find((tab: TabState) => tab.get('active'));
    const activeTabKey = activeTab?.get('preferenceKey') ?? null;

    // Storable file states (containers, plates, boxes, locations) extend FileState directly
    // and don't have a model() method. Use the fileState itself for id/name/permissions.
    // Cast is ok here because we filter out anything that is not a SHARE_LINK_ITEM, and SHARE_LINK_ITEMS is
    // itself filtered from FOLDER_ITEM_TYPES
    const file = isStorableItem ? null : (this.fileState as FolderItemState).model();
    const fileId = isStorableItem ? this.fileState!.id : file!.id;
    const fileName = isStorableItem ? (this.fileState!.name() ?? '') : file!.name();
    const filePerms = isStorableItem ? this.fileState!.permissions()! : file!.permissions();

    renderToDOM(
      <ShareBar
        activeTabKey={activeTabKey}
        fileId={fileId}
        fileName={fileName}
        fileType={fileType}
        onDuplicate={file?.duplicate?.bind(file)}
        filePerms={filePerms}
      />,
      shareBarMount
    );
    this.shareBarMount = shareBarMount;
  }

  unmountShareBar(): void {
    if (this.shareBarMount != null) {
      ReactDOM.unmountComponentAtNode(this.shareBarMount);
    }
  }

  mountPresenceView(): void {
    const presenceMount = this.$('.buffer-tabs-presenceMount')[0];
    if (
      presenceMount == null ||
      EntityStore.currentUser() == null ||
      !isPresenceEnabledForFileState(this.fileState!)
    ) {
      this.unmountPresenceView();
      return;
    }

    const file = this.fileState.model();

    renderToDOM(<ResourcePresenceView resourceId={file.id} />, presenceMount);
    this.presenceMount = presenceMount;
  }

  unmountPresenceView(): void {
    if (this.presenceMount != null) {
      ReactDOM.unmountComponentAtNode(this.presenceMount);
    }
  }

  isRightmostBuffer(): boolean {
    if (this.buffer == null) {
      return false;
    }
    return this.buffer.isRightmostBuffer();
  }

  isWideBuffer(): boolean {
    return this.buffer!.collection.where({active: true}).length === 1;
  }

  update(buffer: BufferState, fileState: FileState): void {
    $(window).off(this._eventNamespace);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    $(window).on(`resize${this._eventNamespace}`, benchlingUtil.resizeThrottler(this.renderItems));
    // eslint-disable-next-line @typescript-eslint/unbound-method
    $(window).on(`buffersResize${this._eventNamespace}`, this.renderItems);

    if (this.buffer != null) {
      this.stopListening(this.buffer);
    }
    const oldFile = isFileStateWithModel(this.fileState) ? this.fileState?.model() : null;
    if (oldFile != null) {
      this.stopListening(oldFile);
    }
    this.buffer = buffer;
    this.fileState = fileState;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.buffer, 'change:shareTabsWithOtherBuffers', this.handleTabDrag);

    if (this.tabs != null) {
      this.stopListening(this.tabs);
    }
    this.tabs = this.buffer.get('tabs');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabs, 'add remove reset', this.renderItems);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabs, 'change:active', this.mountShareBar);

    const file = isFileStateWithModel(this.fileState) ? this.fileState.model() : null;
    if (file != null) {
      let lastWritable = file.permissions?.()?.writable;
      this.listenTo(file, 'change:entry', () => {
        const writable = file.permissions?.()?.writable;
        if (writable !== lastWritable) {
          lastWritable = writable;
          if (this.isRightmostBuffer()) {
            this.mountShareBar();
          }
        }
      });
    }

    if (this.isRightmostBuffer()) {
      this.mountPresenceView();
      this.mountShareBar();
    } else {
      this.unmountPresenceView();
      this.unmountShareBar();
    }

    this.handleTabDrag();
    this.renderItems();
  }
}

BufferNavView.initClass();

/**
 * View that attaches to a div.buffer-container and controls the rendering of buffer tabs and buffer content
 * based off buffer state and tab states.
 */
export class BufferView extends Backbone.View<never> {
  buffer!: BufferState;
  content?: BufferContentView;
  menu!: BufferNavView;
  $bufferOptionsReactMount!: JQuery;
  $bufferContentReactMount!: JQuery;
  renderedTab!: null | TabState;
  tabs?: TabStates;

  override initialize(options?: Backbone.ViewOptions<never>): void {
    super.initialize();
    // @ts-expect-error DefinitelyTyped is not specific enough here. `BackboneView.render` returns `this`, not
    // `BackboneView<TModel extends BackboneModel>`. We don't override render in BufferNavView so we can't
    // narrow the type.
    this.menu = new BufferNavView({el: this.$('.buffer-tabs')}).render();
    this.$bufferContentReactMount = this.$('.buffer-content-reactMount');
    this.$bufferOptionsReactMount = this.$('.buffer-options-reactMount');
    this.renderedTab = null;
  }

  /**
   * If `tab` is the active tab in this buffer, render its content, and render its options view if it has one.
   * @param tab The tab to conditionally render
   * @param shouldForceUpdate Force the rerender of options / content even if the active tab has not changed
   */
  renderTabIfActive(tab?: TabState, shouldForceUpdate: boolean = false): void {
    if (tab == null || !tab.get('active')) {
      return;
    }
    // allow force update for webpack HMR
    if (shouldForceUpdate || this.renderedTab !== tab) {
      const {tabViewMetadata, optionsViewMetadata, dataTestAttrs} = bufferutil.getView(tab);
      /**
       * Sometimes Bootstrap tooltips get 'stuck' when switching tabs. Delete them all. Query selector is
       * global because Bootstrap appends tooltips to the end of `body`.
       *
       * The reason the tooltips get 'stuck' is that something is unsetting the jQuery expando data value for
       * `bs.tooltip` on the element before the destroy fires. When the destroy _does_ fire, it actually
       * creates a new tooltip that then never gets cleaned up.
       *
       * It's unclear if this happens in real life or just in unit tests. In unit tests, the only situation
       * where this failure mode occurred was switching from a tab with a Bootstrap-in-React-in-Backbone
       * tooltip to a fully Backbone tab.
       */
      $('.deprecated-bootstrap-tooltip').remove();
      renderToDOM(
        <TabContent tabState={tab} tabViewMetadata={tabViewMetadata} />,
        this.$bufferContentReactMount[0]
      );
      if (optionsViewMetadata != null) {
        renderToDOM(
          <TabOptions tabState={tab} optionsViewMetadata={optionsViewMetadata} />,
          this.$bufferOptionsReactMount[0]
        );
      } else {
        renderToDOM(
          <TabOptions tabState={null} optionsViewMetadata={null} />,
          this.$bufferOptionsReactMount[0]
        );
      }
      this.$el.toggleClass('grey-tab-view', tab.get('greyTab'));
      this.$el.toggleClass('hide-options-view', optionsViewMetadata == null);
      setTestAttrs(
        this.$el,
        dataTestAttrs ?? {
          component: 'Editor',
          element: 'bufferContainer',
          key: this.isRightmostBuffer() ? 'right' : 'left',
        }
      );

      // Trigger resize event in case options view gets hidden / appears.
      $(window).trigger('buffersResize');
      this.renderedTab = tab;
    }
  }

  isRightmostBuffer(): boolean {
    if (this.buffer == null) {
      return false;
    }
    return this.buffer.isRightmostBuffer();
  }

  renderEmpty(): void {
    if (this.tabs!.size() === 0) {
      this.clearTab();
    }
  }

  clearTab(): void {
    renderToDOM(<TabContent tabState={null} tabViewMetadata={null} />, this.$bufferContentReactMount[0]);
    renderToDOM(<TabOptions tabState={null} optionsViewMetadata={null} />, this.$bufferOptionsReactMount[0]);
    this.renderedTab = null;
  }

  updateBufferAndActiveClass(buffer: BufferState, numActive: number, fileState: FileState): void {
    // If the tab has changed, let's clear the tab first, to ensure we restore
    // the tab with the correct buffer size.
    if (this._activeTab(buffer) !== this.renderedTab) {
      this.clearTab();
    }

    // Update the classes
    this.$el.removeClass('full-width');
    this.$el.removeClass('invisible');
    this.$el.removeClass('vertical-split');
    if (buffer.get('active')) {
      // TODO: This should probably be the responsibility of BuffersView
      if (numActive === 1) {
        this.$el.addClass('full-width');
      } else if (numActive === 2) {
        this.$el.addClass('vertical-split');
      }
      $(window).trigger('buffersResize');
    } else {
      this.$el.addClass('invisible');
    }

    // Update the tab
    this.update(buffer, fileState);
  }

  /**
   * Reset a pane that has no backing buffer: hide it, drop its rendered content so it stops taking up
   * layout height, and detach it from the previous file's state. Without the detach the pane keeps
   * reacting to the old buffer and tabs, and its nav stays live (see `BufferNavView.deactivate`), so a
   * switch from a split file to a single-buffer file leaves the hidden pane running. A later `update`
   * re-attaches everything, so the pane stays reusable. Mirrors the inactive branch of
   * updateBufferAndActiveClass for the classes.
   */
  deactivate(): void {
    this.$el.removeClass('full-width');
    this.$el.removeClass('vertical-split');
    this.$el.addClass('invisible');
    this.clearTab();
    if (this.tabs != null) {
      this.stopListening(this.tabs);
      this.tabs = undefined;
    }
    this.menu.deactivate();
  }

  _activeTab(buffer: BufferState): TabState | undefined {
    return buffer.get('tabs').find((t: TabState) => t.get('active'));
  }

  update(buffer: BufferState, fileState: FileState): void {
    if (this.tabs != null) {
      this.stopListening(this.tabs);
    }
    this.buffer = buffer;
    this.tabs = buffer.get('tabs');
    this.menu.update(buffer, fileState);

    // Activate a tab if there is no active tab
    if (this.tabs!.size() !== 0 && !this._activeTab(buffer)) {
      this.tabs!.at(0).set('active', true);
    }

    this.listenTo(this.tabs, 'add change:active', (tab: TabState) => {
      this.renderTabIfActive(tab);
    });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabs, 'remove reset', this.renderEmpty);

    const tab = this._activeTab(buffer);
    if (tab != null) {
      this.renderTabIfActive(tab);
    } else {
      this.clearTab();
    }
  }
}

/**
 * View that attaches to a div.#buffers element in the editor and controls the rendering of two buffer views
 * for a file.
 */
class BuffersView extends Backbone.View<never> {
  bufferViews!: Array<BufferView>;
  hasReactMounted!: boolean;
  buffers: Optional<BufferStates>;
  fileState!: FileState | null;
  $tabNavbars!: JQuery;
  $containers!: JQuery;
  $reactMount!: JQuery;
  presenceManager!: PresenceManager;
  documentUpdatesManager!: DocumentUpdatesManager;

  static initClass(): void {
    this.prototype.el = '#buffers';
  }

  override initialize(): void {
    this.bufferViews = Array.from(this.$('.buffer-container')).map(
      (bufferEl) => new BufferView({el: bufferEl})
    );
    this.hasReactMounted = false;
    this.buffers = null;
    this.fileState = null;
    this.$tabNavbars = this.$('.buffer-tabs');
    this.$containers = this.$('.buffer-container');
    this.$reactMount = this.$('.buffer-reactMount');
    this.presenceManager = new PresenceManager(this.$('.buffer-presenceManagerMount'));
    this.documentUpdatesManager = new DocumentUpdatesManager();
  }

  update(fileState: FileState | null, reactComponent: ReactElement<unknown> | null = null): void {
    // Clean up previous render
    if (this.buffers != null) {
      this.stopListening(this.buffers);
    }
    if (reactComponent == null && this.hasReactMounted) {
      ReactDOM.unmountComponentAtNode(this.$reactMount[0]);
    }
    if (fileState == null && this.buffers != null) {
      for (const bufferView of this.bufferViews) {
        bufferView.clearTab();
      }
    }
    this.presenceManager.unsubscribe();
    this.documentUpdatesManager.changeActiveDocument(fileState);

    // Update internal state
    this.hasReactMounted = reactComponent != null;
    this.buffers = fileState != null ? fileState.buffers() : undefined;
    this.fileState = fileState;

    // Re-render
    this.$el.toggleClass('empty', fileState == null && reactComponent == null);
    this.$tabNavbars.toggleClass('hidden', fileState != null && fileState.get('hideTabNav'));
    this.$containers.toggleClass('hidden', fileState == null);
    this.$reactMount.toggleClass('hidden', reactComponent == null);
    if (fileState != null) {
      this.listenTo(this.buffers, 'change add remove', () => this.render());
      this.render();
      this.presenceManager.subscribe(this.fileState);
    } else if (reactComponent != null) {
      renderToDOM(reactComponent, this.$reactMount[0]);
    }
  }

  /*
   * Render the current fileState's BufferStates collection into the panes. There are always two panes
   * (one per .buffer-container in the editor.jade template, held in `this.bufferViews`), but the buffer
   * count depends on the file: a split file has two buffers, a single-buffer file (like a procedure)
   * has one. So a pane may have no backing buffer, which is why we iterate over the panes below.
   */
  override render(): this {
    const buffers = this.buffers!.models;
    const numActive = buffers.filter((buffer) => buffer.get('active')).length;
    // A pane with no matching buffer must be deactivated, or it keeps the previous file's split class
    // and rendered content, stays visible below the active pane, and doubles document height — which
    // makes the document scrollable so that focusing a table cell scrolls the header offscreen.
    for (const [i, bufferView] of this.bufferViews.entries()) {
      const buffer = buffers[i];
      if (buffer != null) {
        bufferView.updateBufferAndActiveClass(buffer, numActive, this.fileState!);
      } else {
        bufferView.deactivate();
      }
    }
    return this;
  }
}

BuffersView.initClass();

export {BuffersView, BufferNavView};
export default BuffersView;
